<template>
    <PageSection fixed>
        <PageHeader title="Bulk Upload">
            <button class="btn-primary mr-5" id="DownloadButton" @click="onClickDownload" @error="handleDownloadError">
                Get the template
            </button>
            <button class="btn-primary mr-5" @click.prevent="openFileBrowser">
                Browse to upload
            </button>
        </PageHeader>
        <form ref="form" id="form-upload-booklist" method="POST">
            <input required id="fileInput" type="file" name="file" ref="fileInput" accept=".csv, .tsv" hidden
                @change="handleFormSubmit" />
        </form>
        <CallOut type="warning" class="m4" :message="fileDropError" v-if="fileDropError.length" />
        <CallOut type="warning" class="m4" :message="uploadStateMessage" v-if="hasFailed" />
        <LoadingDialog v-if="isUploading" label="File Loading">
            Your file is being uploaded and verified. This should only take about a
            minute. Please do not press back on your browser.
        </LoadingDialog>
        <div v-show="isOverDropZone"
            class="fixed inset-0 bg-overlay z-100 flex justify-center items-center pointer-events-none">
            <div class="heading-1 text-white">Drop file to upload</div>
        </div>
        <Card>
            <TableLayout>
                <Table :table="table" :show-loading-spinner="loading" aria-describedby="page-header" class="b-style-none b-r-style-none b-b-style-solid">
                    <thead class="table-thead">
                        <tr class="flex" v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id">
                            <TableHeaderCell v-for="header in headerGroup.headers" :header="header">
                            </TableHeaderCell>
                        </tr>
                    </thead>
                    <tbody class="grid">
                        <tr class="table-row" v-for="row in rows" :key="row.id" v-on:click="onCellClick(row)">
                            <TableDataCell v-for="cell in row.getVisibleCells()" :cell="cell" cursor="pointer">
                                <template #status>
                                    <Tag :variant="getStatusChipType(cell.row.original.status)">{{
                                        cell.row.original.status
                                    }}</Tag>
                                </template>
                            </TableDataCell>
                        </tr>
                    </tbody>
                    <template #empty>
                        <EmptyState :icon="message.icon" :title="message.title" :text="message.description">
                        </EmptyState>
                    </template>
                </Table>
                <template #paginator>
                    <Paginator :table="table" :page-sizes="[5, 10, 25, 50, 100, 200]" class="!b-0" />
                </template>
            </TableLayout>
        </Card>
    </PageSection>
</template>

<script setup lang="ts">
import {
	ColumnDef,
	functionalUpdate,
	getCoreRowModel,
	Row,
	useVueTable,
} from "@tanstack/vue-table";
import { useDropZone } from "@vueuse/core";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { definePage } from "vue-router/auto";
import { TagVariants } from "../../../../../shared/components/Tag.vue";
import { capture } from "../../../../../shared/lib/posthog";
import { useFileUpload } from "../../../composables";
import { useBooklistTable } from "../../../composables/useUploadHistoryTable";
import { getPreferredTablePageSize } from "../../../lib/local-storage";
import { useInstitution } from "../../../stores/institution";
import { trpc } from "../../../trpc/client";
import { CSVUpload } from "../../../types";

const institution = useInstitution();

const form = ref<HTMLFormElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const dropZoneRef = ref<HTMLElement>(document.body);
const fileDropError = ref("");
const router = useRouter();

//@ts-ignore - Need to figure out what is wrong with this type
const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);
const {
	rows: csvUploadHistory,
	totalRowsCount,
	loading,
	message,
	fetchRows,
	columnPinning,
	pagination,
} = useBooklistTable();

const { upload, isUploading, hasFailed, confirm, cancel, snapshot } =
	useFileUpload({
		onSuccess: (uploadId: string) => {
			router.push(`/booklist/upload/${uploadId}`);
		},
	});

const uploadStateMessage = computed(() => {
	const errors = snapshot.value.context.errors;
	return errors ? errors[0].message : "";
});

async function onClickDownload() {
	capture({ event: "Booklist Template download clicked" });
	try {
		const csvString =
			await trpc.booklistUpload.downloadTemplatePerInstitution.query();
		const blob = new Blob([csvString], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "adoptions-upload-template.csv";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		handleDownloadSuccess();
	} catch (error) {
		handleDownloadError(error);
	}
}

function handleDownloadSuccess() {
	capture({ event: "Booklist Template download succeeded" });
}

function handleDownloadError(error: unknown) {
	if (error instanceof Error) {
		capture({
			event: "Booklist Template download failed",
			error: error.message,
		});
	} else {
		capture({
			event: "Booklist Template download failed",
			error: "An unknown error occurred",
		});
	}
}

const headers: ColumnDef<CSVUpload>[] = [
	{
		id: "id",
		header: "Row",
		size: 80,
		cell: ({ row, table }) => {
			const { pageIndex, pageSize } = table.getState().pagination;
			return pageIndex * pageSize + row.index + 1;
		},
	},
	{
		id: "date",
		header: "Date",
		accessorKey: "date",
		size: 150,
	},
	{
		id: "fileName",
		header: "Name",
		accessorKey: "fileName",
		size: 280,
	},
	{
		id: "user_full_name",
		header: "Uploader",
		accessorKey: "user_full_name",
		size: 230,
	},
	{
		id: "count.rows",
		header: "Row Count",
		accessorKey: "count.rows",
	},
	{
		id: "count.books",
		header: "Book Count",
		accessorKey: "count.books",
	},
	{
		id: "count.users",
		header: "User Count",
		accessorKey: "count.users",
	},
	{
		id: "count.courses",
		header: "Course Count",
		accessorKey: "count.courses",
	},
	{
		id: "count.errors",
		header: "Error Count",
		accessorKey: "count.errors",
	},
	{
		id: "status",
		header: "Status",
		size: 150,
		accessorKey: "status",
	},
];

function getStatusChipType(status: CSVUpload["status"]): TagVariants {
	if (status === "invalid" || status === "failed") {
		return "negative";
	} else if (status === "confirmed") {
		return "positive";
	}
	return "positiveAlt";
}

function openFileBrowser() {
	if (!fileInput.value) return;
	fileInput.value.click();
}

function handleFormSubmit() {
	if (form.value !== null) {
		upload(new FormData(form.value as HTMLFormElement));
		form.value.reset();
		fileDropError.value = "";
	}
}

function onCellClick(row: Row<CSVUpload>) {
	router.push(`/booklist/upload/${row.original.id}`);
}

function onDrop(files: File[] | null) {
	// called when files are dropped on zone
	fileDropError.value = "";
	if (!files || !files.length) {
		return;
	}

	if (files.length > 1) {
		fileDropError.value =
			"Multi-file upload is not supported. Please drop only one file at a time.";
		return;
	}

	const [file] = files;

	if (file.type !== "text/csv" && file.type !== "text/tab-separated-values") {
		fileDropError.value =
			"Only CSV and TSV file uploads are supported. Please try again.";
		return;
	}

	const formData = new FormData();
	formData.append("file", file);
	upload(formData);
}

onMounted(() => {
	fetchRows({
		pagination: {
			pageIndex: 1,
			pageSize: getPreferredTablePageSize(),
		},
		sorting: [],
	});
});

const table = useVueTable<CSVUpload>({
	getCoreRowModel: getCoreRowModel(),
	manualPagination: true,
	columnResizeMode: "onChange",
	getRowId: (row) => row.id,
	enableSorting: false,
	get data() {
		return csvUploadHistory.value;
	},
	get columns() {
		return headers;
	},
	get rowCount() {
		return totalRowsCount.value;
	},
	state: {
		get pagination() {
			return pagination.value;
		},
		get columnPinning() {
			return columnPinning.value;
		},
	},
	onPaginationChange(updater) {
		const newPagination = functionalUpdate(updater, pagination.value);
		fetchRows({
			pagination: {
				pageIndex: newPagination.pageIndex + 1,
				pageSize: newPagination.pageSize,
			},
			sorting: [],
		});

		window.localStorage.setItem(
			"preferredTablePageSize",
			newPagination.pageSize.toString(),
		);
	},
});

const rows = computed(() => table.getCoreRowModel().rows);

definePage({
	meta: {
		requiresAuth: true,
		permission: "booklist:bulkUpload",
		fixed: true,
	},
});
</script>

<style scoped lang="postcss">
:deep .placeholder path:not(mask > path) {
    @apply fill-gray-800;
}

:deep(tbody tr) {
    cursor: pointer;
}

.grid-container {
    grid-template: [row1-start] "header"[row1-end] [row2-start] "content"[row2-end] / 1fr auto;
}
</style>
